/* EquiLife product card: in-card quick add (desktop) / centered modal (mobile,
   comp 576:438 = the desktop 319px panel scaled to 379px).
   The panel contains a real Shopify product form inside Symmetry's
   <product-form> element, so adding to cart, drawer refresh and errors are
   handled by the theme exactly as on the PDP. This element only manages:
   open/close, option chips -> variant, price rendering, and the
   subscribe / one-time toggle (selling_plan + Skio line properties). */
(function () {
  if (customElements.get('el-product-card')) return;

  const MQ_MOBILE = '(max-width: 749px)';
  const TOAST_MS = parseInt((document.querySelector('el-product-card') || {}).dataset ? (document.querySelector('el-product-card').dataset.toastMs || '5000') : '5000', 10);
  let openCard = null;
  let overlay = null;

  const money = (cents) => {
    // USD-only by design (same note as the PDP subscribe box).
    const n = (cents / 100).toFixed(2);
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // S20-127 shared savings percent: mirrors snippets/el-save-pct.liquid exactly (same
  // integer operations, same order). save and ref are cents. Returns '17' or '17.5', or
  // '' when no percent may be shown.
  // D2: floor to 0.5 in integer half-steps, Math.floor(save * 200 / ref); a float
  //   save / ref * 100 can land just under a whole number (0.29 * 100 = 28.999...).
  // D20: if ref * (h + 1) - save * 200 < 200 (the price is within one cent of the exact
  //   (h + 1) / 2 percent discount) use h + 1; never above 200 half-steps (100%).
  // D14: '' when the final h is 0, and '' when save <= 0.
  const savePctText = (save, ref) => {
    if (!(save > 0) || !(ref > 0)) return '';
    let h = Math.floor(save * 200 / ref);
    if (ref * (h + 1) - save * 200 < 200 && h < 200) h += 1;
    if (h === 0) return '';
    return h % 2 === 0 ? String(h / 2) : (h / 2).toFixed(1);
  };

  class ElProductCard extends HTMLElement {
    q(sel) { return this.querySelector(sel) || (this.panel ? this.panel.querySelector(sel) : null); }
    qa(sel) { return [...this.querySelectorAll(sel), ...(this.panel && !this.contains(this.panel) ? this.panel.querySelectorAll(sel) : [])]; }

    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-el-card-json]').textContent);
      this.panel = this.querySelector('.el-card__panel');
      // Change 2 (2026-09-30) guard: cta_mode 'shop_now' renders no .el-card__panel
      // (no quick-add form on that card). Everything below that reads this.panel is
      // already null-safe via the `q()`/`qa()` helpers or an explicit `if`, except
      // the two spots guarded here, which would otherwise throw on a null panel.
      if (this.panel) {
        this.panelHome = document.createComment('el-card-panel-home');
        this.panel.parentNode.insertBefore(this.panelHome, this.panel);
      }
      this.form = this.querySelector('form.js-product-form');
      this.idInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.planSelect = this.querySelector('[data-el-quick-add-plan]');
      this.props = this.querySelectorAll('[data-el-quick-add-prop]');
      this.subBox = this.querySelector('.el-quick-add__sub');
      // S20-376: plan ids offered by the Subscribe option (one selling plan group)
      this.planIds = this.planSelect ? [...this.planSelect.options].map((o) => String(o.value)) : [];
      this.forcedOnce = !!(this.subBox && this.subBox.hidden);
      this.atc = this.querySelector('.el-quick-add__atc');
      this.selected = {};
      (this.data.options || []).forEach((name, i) => { this.selected[i] = this.data.current.options[i]; });

      this.querySelectorAll('[data-el-card-open]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); this.open(); }));
      const direct = this.querySelector('[data-el-card-direct-add]');
      if (direct) direct.addEventListener('click', () => this.directAdd(direct));
      this.querySelectorAll('[data-el-card-close]').forEach((el) => el.addEventListener('click', (e) => { e.preventDefault(); this.close(); }));
      this.querySelectorAll('.el-quick-add__chip').forEach((chip) => chip.addEventListener('click', () => this.pick(chip)));
      this.querySelectorAll('.el-quick-add__radio').forEach((r) => r.addEventListener('change', () => this.syncMode()));
      if (this.planSelect) this.planSelect.addEventListener('change', () => this.render());
      if (this.panel) {
        this.panel.addEventListener('on:cart:add', () => this.onAdded());
        this.panel.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('is-open')) this.close(); });
      }

      this.syncMode();
      this.render();

      // narrow mode (<300px wide: home carousels, dense grids, phones) scales the resting card
      // Round 3 (2026-09-30) guard: shop_now cards (el-card--shop-now) opt out entirely - the
      // Figma Band 3 layout (Change 4, assets/el-product-card.css) is a different, fluid design
      // that must never receive the generic narrow-card compaction, at any width.
      const setNarrow = () => {
        if (this.classList.contains('el-card--shop-now')) return;
        this.classList.toggle('is-narrow', this.getBoundingClientRect().width < 300);
      };
      setNarrow();
      if ('ResizeObserver' in window) { this.ro = new ResizeObserver(setNarrow); this.ro.observe(this); }
      else window.addEventListener('resize', setNarrow);
    }

    directAdd(btn) {
      if (btn.getAttribute('aria-disabled') === 'true' || !this.form) return;
      this.form.requestSubmit();
    }

    fitModal() {
      // mobile modal: the desktop panel scaled up to the comp's 379px, or to the viewport minus the comp's 11px margins
      const w = Math.min(379, window.innerWidth - 22);
      this.panel.style.setProperty('--el-qa-zoom', (w / 319).toFixed(5));
    }

    toast(el, ms) {
      if (!el) return Promise.resolve();
      el.hidden = false;
      return new Promise((resolve) => setTimeout(() => { el.hidden = true; resolve(); }, ms));
    }

    onAdded() {
      const cardToast = this.querySelector('[data-el-card-toast]');
      const panelToast = this.q('[data-el-quick-add-toast]');
      if (this.classList.contains('is-open') && this.classList.contains('is-sheet')) {
        // sheet / dialog: confirm inside the panel, then close and confirm on the card
        this.toast(panelToast, Math.min(TOAST_MS, 2500)).then(() => { this.close(); this.toast(cardToast, TOAST_MS); });
      } else {
        if (this.classList.contains('is-open')) this.close();
        this.toast(cardToast, TOAST_MS);
      }
    }

    open() {
      if (openCard && openCard !== this) openCard.close();
      openCard = this;
      const mobile = window.matchMedia(MQ_MOBILE).matches;
      const sheet = mobile || this.getBoundingClientRect().width < 300;
      this.classList.toggle('is-sheet', sheet);
      this.classList.add('is-open');
      this.panel.removeAttribute('aria-hidden');
      this.panel.setAttribute('aria-modal', sheet ? 'true' : 'false');
      if (sheet) {
        // overlay first, then the panel: same stacking level on phones (8000), so DOM order puts the panel on top
        if (!overlay) {
          overlay = document.createElement('div');
          overlay.className = 'el-sheet-overlay';
          overlay.addEventListener('click', () => { if (openCard) openCard.close(); });
        }
        document.body.appendChild(overlay);
        document.body.appendChild(this.panel);
        this.panel.classList.add('el-card__panel--portal');
        if (mobile) {
          this.panel.classList.add('el-card__panel--mobile');
          this.fitModal();
          this.onResize = () => this.fitModal();
          window.addEventListener('resize', this.onResize);
        }
        document.body.classList.add('el-sheet-open');
      }
      const focusTarget = this.q('.el-quick-add__close');
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      this.classList.remove('is-sheet');
      this.panel.setAttribute('aria-hidden', 'true');
      if (this.panel.classList.contains('el-card__panel--portal')) {
        this.panel.classList.remove('el-card__panel--portal', 'el-card__panel--mobile');
        this.panel.style.removeProperty('--el-qa-zoom');
        if (this.onResize) { window.removeEventListener('resize', this.onResize); this.onResize = null; }
        this.panelHome.parentNode.insertBefore(this.panel, this.panelHome.nextSibling);
      }
      document.body.classList.remove('el-sheet-open');
      if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (openCard === this) openCard = null;
    }

    pick(chip) {
      const idx = parseInt(chip.closest('.el-quick-add__opt').dataset.index, 10);
      this.selected[idx] = chip.dataset.value;
      this.render();
    }

    currentVariant() {
      const opts = this.data.options.map((_, i) => this.selected[i]);
      return this.data.variants.find((v) => v.options.every((o, i) => o === opts[i])) || null;
    }

    mode() {
      const checked = this.q('.el-quick-add__radio:checked');
      return checked ? checked.value : 'subscribe';
    }

    // S20-376: a variant with no allocation for the offered plans can't be
    // subscribed to: hide Subscribe (plan picker) and select One-time; switching
    // back re-shows it and restores Subscribe if it was only deselected for that.
    applySubscribable(v) {
      if (!this.subBox) return;
      const ok = (v.allocations || []).some((a) => this.planIds.indexOf(String(a.plan)) !== -1);
      if (ok === !this.subBox.hidden) return;
      const subRadio = this.subBox.querySelector('.el-quick-add__radio');
      const onceRadio = this.q('.el-quick-add__once .el-quick-add__radio');
      if (!ok) {
        this.forcedOnce = !!(subRadio && subRadio.checked);
        if (onceRadio) onceRadio.checked = true;
      } else if (this.forcedOnce) {
        if (subRadio) subRadio.checked = true;
        this.forcedOnce = false;
      }
      this.subBox.hidden = !ok;
      const sub = this.mode() === 'subscribe' && !!this.planSelect;
      if (this.planSelect) this.planSelect.disabled = !sub;
      this.props.forEach((p) => { p.disabled = !sub; });
      this.subBox.classList.toggle('is-off', !sub);
    }

    syncMode() {
      const sub = this.mode() === 'subscribe' && !!this.planSelect;
      if (this.planSelect) this.planSelect.disabled = !sub;
      this.props.forEach((p) => { p.disabled = !sub; });
      if (this.subBox) this.subBox.classList.toggle('is-off', !sub);
      this.render();
    }

    render() {
      const v = this.currentVariant();
      // chips: selected + availability (a value is unavailable if no available variant carries it with the other selections)
      this.qa('.el-quick-add__opt').forEach((optEl) => {
        const idx = parseInt(optEl.dataset.index, 10);
        optEl.querySelectorAll('.el-quick-add__chip').forEach((chip) => {
          chip.classList.toggle('is-selected', chip.dataset.value === this.selected[idx]);
          const test = { ...this.selected, [idx]: chip.dataset.value };
          const match = this.data.variants.find((vv) => vv.options.every((o, i) => o === test[i]));
          chip.classList.toggle('is-unavailable', !match || !match.available);
        });
        const val = optEl.querySelector('[data-el-quick-add-optval]');
        if (val) val.textContent = this.selected[idx] || '';
      });

      if (!v) {
        if (this.atc) { this.atc.setAttribute('aria-disabled', 'true'); this.atc.textContent = this.data.copy.unavailable; }
        return;
      }
      if (this.idInput) this.idInput.value = v.id;
      this.applySubscribable(v);

      // S20-127 promo price (variant metafield custom.promo_price, cents or null).
      // Mirrors the Liquid in snippets/el-product-card.liquid; keep the two in step.
      // Reference (struck) price: compare-at when above the price (D5), else the price (D9).
      const promo = typeof v.promo_price === 'number' && v.promo_price > 0 ? v.promo_price : null;
      const ref = v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : v.price;

      // one-time prices (D4: min(price, promo); subscription prices below are unchanged)
      const oncePrice = this.q('[data-el-quick-add-once-price]');
      const onceCmp = this.q('[data-el-quick-add-once-compare]');
      const onceLow = promo !== null && promo < v.price ? promo : v.price;
      if (oncePrice) oncePrice.textContent = money(onceLow);
      if (onceCmp) { const struck = ref > onceLow; onceCmp.textContent = struck ? money(ref) : ''; onceCmp.hidden = !struck; }

      // subscription prices from the Skio allocation of the selected plan
      if (this.planSelect) {
        const planId = this.planSelect.value;
        const alloc = (v.allocations || []).find((a) => String(a.plan) === String(planId));
        const subPrice = this.q('[data-el-quick-add-sub-price]');
        const subCmp = this.q('[data-el-quick-add-sub-compare]');
        const b1 = this.q('[data-el-quick-add-bullet-first]');
        const b2 = this.q('[data-el-quick-add-bullet-ongoing]');
        if (alloc) {
          if (subPrice) subPrice.textContent = money(alloc.price);
          if (subCmp) { const c = alloc.compare_at_price || v.price; subCmp.textContent = money(c); subCmp.hidden = c <= alloc.price; }
          if (b1) { b1.querySelector('[data-amount]').textContent = money(alloc.first_price != null ? alloc.first_price : alloc.price); }
          if (b2) { const ongoing = alloc.ongoing_price != null ? alloc.ongoing_price : alloc.per_delivery_price; b2.querySelector('[data-amount]').textContent = money(ongoing); b2.hidden = alloc.ongoing_price == null && alloc.first_price == null; }
        }
      }

      // sale label + You Save
      const saleEl = this.q('[data-el-quick-add-sale]');
      const saveEls = this.querySelectorAll('[data-el-card-save]');
      const cardPrice = this.querySelector('[data-el-card-price]');
      const cardCmp = this.querySelector('[data-el-card-compare]');
      // D15: the back-panel Sale label follows the One-time slot: (ref - onceLow) / ref,
      // same percent rules as You Save; blank (slot kept, no text) when nothing to show.
      const salePct = savePctText(ref - onceLow, ref);
      if (saleEl) { saleEl.classList.toggle('is-blank', !salePct); saleEl.textContent = salePct ? this.data.copy.sale.replace('[pct]', salePct) : ''; }
      const subLead = this.querySelector('[data-el-card-sub-lead]');
      const lead = subLead && this.data.first_plan ? (v.allocations || []).find((x) => String(x.plan) === String(this.data.first_plan)) : null;
      // Collections rule: the promo leads only if below the price AND below the card's
      // subscription price (first_plan allocation, D10) when the variant has one.
      const promoLeads = promo !== null && promo < v.price && (!lead || promo < lead.price);
      let lowest = v.price;
      if (lead && lead.price < lowest) lowest = lead.price;
      if (promoLeads && promo < lowest) lowest = promo;
      // You Save = reference - lowest; percent from savePctText (D2, D20, D14). Dollar amount
      // is not rounded. Hidden (and emptied) when there is no percent to show.
      const saveAmt = ref - lowest;
      const pctText = savePctText(saveAmt, ref);
      saveEls.forEach((el) => { el.hidden = !pctText; el.textContent = pctText ? this.data.copy.save.replace('[pct]', pctText).replace('[amount]', money(saveAmt)) : ''; });
      // lead price: the promo when it qualifies, else the plan price on subscription cards
      // (marketing 2026-09-24), else the one-time price; strike the reference when above it
      const leadPrice = promoLeads ? promo : (lead ? lead.price : v.price);
      if (cardPrice) cardPrice.textContent = money(leadPrice);
      if (cardCmp) { const struck = ref > leadPrice; cardCmp.textContent = struck ? money(ref) : ''; cardCmp.hidden = !struck; }

      if (this.atc) {
        if (v.available) { this.atc.removeAttribute('aria-disabled'); this.atc.textContent = this.data.copy.atc; }
        else { this.atc.setAttribute('aria-disabled', 'true'); this.atc.textContent = this.data.copy.soldout; }
      }
    }
  }

  customElements.define('el-product-card', ElProductCard);

  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && openCard) openCard.close(); });
  document.addEventListener('click', (e) => {
    if (!openCard || openCard.classList.contains('is-sheet')) return;
    if (!openCard.contains(e.target) && !openCard.panel.contains(e.target)) openCard.close();
  });
})();
